import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import EnhancedNavigation from '../navigation/EnhancedNavigation';
import BreadcrumbNavigation from '../navigation/BreadcrumbNavigation';
import useKeyboardNavigation from '../../hooks/useKeyboardNavigation';
import Icon from '../AppIcon';
import HiredNotificationProvider from '../notifications/HiredNotificationProvider';
import Footer from './Footer';

const NavigationLayout = ({ children, customBreadcrumbs = null, showSidebar = true }) => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Initialize keyboard navigation (now inside Router context)
  useKeyboardNavigation();

  // Handle scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Determine if we should show navigation based on route
  const shouldShowNavigation = () => {
    const hideNavRoutes = ['/login-register', '/', '/home', '/how-it-works', '/skills-directory'];
    return !hideNavRoutes.includes(location.pathname);
  };

  if (!shouldShowNavigation()) {
    return (
      <div className="min-h-screen flex flex-col">
        <HiredNotificationProvider />
        <div className="flex-1">
          {children}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Enhanced Navigation Header */}
      <EnhancedNavigation />
      
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation customBreadcrumbs={customBreadcrumbs} />
      
      <div className="flex flex-1">
        {/* Main Content Area */}
        <div className="flex-1">
          {/* Page Content */}
          <main>
            {children}
          </main>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 w-10 h-10 bg-white text-gray-600 rounded-full shadow-lg hover:bg-gray-50 hover:text-gray-900 transition-colors z-30 flex items-center justify-center border border-gray-200"
          aria-label="Scroll to top"
        >
          <Icon name="ArrowUp" size={16} />
        </button>
      )}

      {/* Hired Notification System */}
      <HiredNotificationProvider />

      {/* Keyboard Navigation Hints (Development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 left-4 bg-black bg-opacity-75 text-white text-xs p-2 rounded hidden lg:block">
          <div>Keyboard shortcuts:</div>
          <div>Alt + N: Navigation</div>
          <div>Alt + S: Search</div>
          <div>Alt + H: Home</div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default NavigationLayout;
